import { type QueryRef, useReadQuery, useSuspenseQuery } from '@apollo/client/react';
import { ShieldCheckIcon } from 'lucide-react';

import { AppRequestsKindEnum } from 'src/graphql/schema';
import { AppsDocument, type IAppsQuery, type IAppsQueryVariables } from 'src/graphql/actions/apps/apps.query';
import { AppRequestsDocument } from 'src/graphql/actions/appRequests/appRequests.query';
import { withSuspense } from 'src/hoc';
import { pluralize } from 'src/utils/pluralize';
import { UnclaimedBadge } from 'src/widgets/AppCard';

import { InfoCard, InfoCardSkeleton } from '../../ui';

const ApplicationsCardSkeleton = () => <InfoCardSkeleton statsCount={3} withFooter />;

type Props = {
  unclaimedQueryRef: QueryRef<IAppsQuery, IAppsQueryVariables>;
};

const ApplicationsCard = ({ unclaimedQueryRef }: Props) => {
  const {
    data: { apps: yourApps },
  } = useSuspenseQuery(AppsDocument, { fetchPolicy: 'cache-and-network', variables: { owned: true, perPage: 0 } });
  const {
    data: { apps: vendorApps },
  } = useSuspenseQuery(AppsDocument, { fetchPolicy: 'cache-and-network', variables: { owned: false, perPage: 0 } });
  const {
    data: { appRequests: receivedRequests },
  } = useSuspenseQuery(AppRequestsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { kind: AppRequestsKindEnum.Received, perPage: 0 },
  });
  const {
    data: { apps: unclaimedApps },
  } = useReadQuery(unclaimedQueryRef);

  const stats = [
    { label: 'Yours', value: yourApps.nodesCount },
    { label: "Vendor's", value: vendorApps.nodesCount },
    { label: 'Requests', value: receivedRequests.nodesCount, className: 'border-l pl-5 ml-auto text-right' },
  ];

  return (
    <InfoCard
      icon={ShieldCheckIcon}
      title="Applications"
      totalCount={yourApps.nodesCount + vendorApps.nodesCount}
      stats={stats}
    >
      {Boolean(unclaimedApps.nodesCount) && (
        <div className="flex items-center flex-wrap gap-2 pt-2">
          <UnclaimedBadge />
          <span className="text-xs text-muted-foreground">
            {pluralize(unclaimedApps.nodesCount, 'application', 'applications')} left to set up
          </span>
        </div>
      )}
    </InfoCard>
  );
};

export default withSuspense(ApplicationsCard, ApplicationsCardSkeleton);
